import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { RestaurantCard } from "@/components/restaurant-card";
import { Plus, Heart, MapPin, TrendingUp, Filter, Crown, Trophy, Medal, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, X } from "lucide-react";
import heroImage from "@/assets/hero-restaurant.jpg";
import { getRestaurants, normalizeRestaurantAreas } from "@/lib/store";
import type { Restaurant } from "@/types";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
// Sliderは使わず、最低総合点はSelectで選択
import { estimatePriceValue, cn, compareDropdown, normalizeMunicipalityLabel, normalizePrefectureLabel } from "@/lib/utils";
import { compositeScore20, scoreToneClass } from "@/lib/rating";
import NearbySuggest from "@/components/nearby-suggest";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { useIsMobile } from "@/hooks/use-mobile";
import { buildAllRankingDefs } from "@/lib/rankings";
import { Link } from "react-router-dom";

export function Dashboard() {
  const isMobile = useIsMobile();
  const [filtersOpen, setFiltersOpen] = useState<boolean>(true);
  const [areaCleanupStatus, setAreaCleanupStatus] = useState<string>("");
  const [areaCleanupRunning, setAreaCleanupRunning] = useState(false);
  useEffect(() => {
    // モバイルはデフォルト閉じる、タブレット以上は開く
    setFiltersOpen(!isMobile);
  }, [isMobile]);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTabParam = (searchParams.get('tab') === 'wishlist') ? 'wishlist' : 'visited';
  const [activeTab, setActiveTab] = useState<"visited" | "wishlist">(initialTabParam as any);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  // URL クエリから初期値を復元（ブラウザバックで状態維持）
  const [search, setSearch] = useState(() => searchParams.get('q') ?? "");
  const [filterCuisine, setFilterCuisine] = useState<string>(() => searchParams.get('cuisine') ?? "all");
  const [filterPref, setFilterPref] = useState<string>(() => {
    const pref = normalizePrefectureLabel(searchParams.get('pref'));
    return pref || "all";
  });
  const [filterMuni, setFilterMuni] = useState<string>(() => {
    const muni = normalizeMunicipalityLabel(searchParams.get('muni'));
    return muni || "all";
  });
  const [minRating, setMinRating] = useState<number>(() => {
    const v = Number(searchParams.get('min') ?? '0');
    return Number.isFinite(v) ? v : 0;
  });
  const [sortKey, setSortKey] = useState<
    "newest" | "rating_desc" | "rating_asc" | "price_asc" | "price_desc" | "name_asc"
  >(() => (searchParams.get('sort') as any) || "newest");
  const [page, setPage] = useState<number>(() => {
    const v = Number(searchParams.get('page') ?? '1');
    return Number.isFinite(v) && v > 0 ? Math.floor(v) : 1;
  });
  const [pageSize, setPageSize] = useState<number>(() => {
    const v = Number(searchParams.get('per') ?? '12');
    return [6, 12, 18, 24, 36, 48].includes(v) ? v : 12;
  });
  const navigate = useNavigate();

  useEffect(() => {
    setRestaurants(getRestaurants());
    const onStorage = (e: StorageEvent) => {
      if (!e.key || e.key === "ccq:restaurants") setRestaurants(getRestaurants());
    };
    const onCustom = () => setRestaurants(getRestaurants());
    window.addEventListener("storage", onStorage);
    window.addEventListener('ccq:restaurants-changed' as any, onCustom as any);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener('ccq:restaurants-changed' as any, onCustom as any);
    };
  }, []);

  // タブ状態をURL ?tab=... に同期（戻る・再読み込みしても維持）
  // フィルタ/検索/ソートの状態を URL に反映
  useEffect(() => {
    const t = activeTab === 'wishlist' ? 'wishlist' : 'visited';
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    if (search) next.set('q', search); else next.delete('q');
    if (filterCuisine && filterCuisine !== 'all') next.set('cuisine', filterCuisine); else next.delete('cuisine');
    if (filterPref && filterPref !== 'all') next.set('pref', filterPref); else next.delete('pref');
    if (filterMuni && filterMuni !== 'all') next.set('muni', filterMuni); else next.delete('muni');
    if (minRating && minRating > 0) next.set('min', String(minRating)); else next.delete('min');
    if (sortKey && sortKey !== 'newest') next.set('sort', sortKey); else next.delete('sort');
    if (page && page > 1) next.set('page', String(page)); else next.delete('page');
    if (pageSize && pageSize !== 12) next.set('per', String(pageSize)); else next.delete('per');
    setSearchParams(next, { replace: true });
  }, [activeTab, search, filterCuisine, filterPref, filterMuni, minRating, sortKey, page, pageSize]);

  // 絞り込みや並び替えが変わったらページを先頭へ戻す
  useEffect(() => {
    setPage(1);
  }, [activeTab, search, filterCuisine, filterPref, filterMuni, minRating, sortKey]);

  const visitedRestaurants = useMemo(
    () => restaurants.filter((r) => !r.isWishlist),
    [restaurants]
  );
  const wishlistRestaurants = useMemo(
    () => restaurants.filter((r) => r.isWishlist),
    [restaurants]
  );
  const currentRestaurants = activeTab === "visited" ? visitedRestaurants : wishlistRestaurants;

  const averageRating = useMemo(() => {
    const scores = visitedRestaurants.map((r) => compositeScore20(r)).filter((v) => v > 0);
    if (scores.length === 0) return 0;
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
    return Math.round(avg * 10) / 10; // 0..20 の平均を小数1桁
  }, [visitedRestaurants]);

  const cuisines = useMemo(() => {
    const set = new Set<string>();
    restaurants.forEach(r => r.cuisine && set.add(r.cuisine));
    return Array.from(set).sort(compareDropdown);
  }, [restaurants]);

  const prefectures = useMemo(() => {
    const set = new Set<string>();
    restaurants.forEach(r => {
      const pref = normalizePrefectureLabel(r.prefecture);
      if (pref) set.add(pref);
    });
    return Array.from(set).sort(compareDropdown);
  }, [restaurants]);

  const municipalities = useMemo(() => {
    const set = new Set<string>();
    restaurants.forEach(r => {
      if (filterPref !== 'all') {
        if (normalizePrefectureLabel(r.prefecture) !== filterPref) return;
      }
      const muni = normalizeMunicipalityLabel(r.municipality);
      if (muni) set.add(muni);
    });
    return Array.from(set).sort(compareDropdown);
  }, [restaurants, filterPref]);

  function computeScore(r: Restaurant) {
    return compositeScore20(r); // 0..20
  }

  function getUpdatedAt(r: Restaurant): number {
    if (r.updatedAt) {
      const t = Date.parse(r.updatedAt);
      if (!isNaN(t)) return t;
    }
    if (r.createdAt) {
      const t = Date.parse(r.createdAt);
      if (!isNaN(t)) return t;
    }
    if (typeof r.id === 'string' && /^\d{10,}/.test(r.id)) {
      const ms = parseInt(r.id.split('-')[0], 10);
      if (!isNaN(ms)) return ms;
    }
    return 0;
  }

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const min = minRating || 0;
    let list = currentRestaurants.filter(r => {
      if (filterCuisine !== 'all' && r.cuisine !== filterCuisine) return false;
      const pref = normalizePrefectureLabel(r.prefecture);
      const muni = normalizeMunicipalityLabel(r.municipality);
      if (filterPref !== 'all' && pref !== filterPref) return false;
      if (filterMuni !== 'all' && muni !== filterMuni) return false;
      if (q) {
        const hay = `${r.name} ${r.notes || ''} ${r.location || ''} ${pref} ${muni}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const sc = computeScore(r);
      if (sc < min) return false;
      return true;
    });
    list = list.slice();
    list.sort((a, b) => {
      switch (sortKey) {
        case 'newest':
          // 仕様変更: 作成順ではなく「更新順（updatedAt）」に並べる
          return getUpdatedAt(b) - getUpdatedAt(a);
        case 'rating_desc':
          return computeScore(b) - computeScore(a);
        case 'rating_asc':
          return computeScore(a) - computeScore(b);
        case 'name_asc':
          return a.name.localeCompare(b.name, 'ja');
        case 'price_asc': {
          const pa = estimatePriceValue(a.priceRange) ?? Number.POSITIVE_INFINITY;
          const pb = estimatePriceValue(b.priceRange) ?? Number.POSITIVE_INFINITY;
          return pa - pb;
        }
        case 'price_desc': {
          const pa = estimatePriceValue(a.priceRange) ?? Number.NEGATIVE_INFINITY;
          const pb = estimatePriceValue(b.priceRange) ?? Number.NEGATIVE_INFINITY;
          return pb - pa;
        }
        default:
          return 0;
      }
    });
    return list;
  }, [currentRestaurants, search, filterCuisine, filterPref, filterMuni, minRating, sortKey]);

  const total = filteredSorted.length;
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  const end = start + pageSize;
  const paged = filteredSorted.slice(start, end);

  const activeFilterChips = [
    search ? { key: 'q', label: `検索: ${search}`, clear: () => setSearch("") } : null,
    filterCuisine !== 'all' ? { key: 'cuisine', label: filterCuisine, clear: () => setFilterCuisine('all') } : null,
    filterPref !== 'all' ? { key: 'pref', label: filterPref, clear: () => setFilterPref('all') } : null,
    filterMuni !== 'all' ? { key: 'muni', label: filterMuni, clear: () => setFilterMuni('all') } : null,
    minRating > 0 ? { key: 'min', label: `${minRating}点以上`, clear: () => setMinRating(0) } : null,
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[];

  async function runAreaCleanup() {
    setAreaCleanupRunning(true);
    setAreaCleanupStatus("");
    try {
      const result = await normalizeRestaurantAreas();
      setRestaurants(getRestaurants());
      setAreaCleanupStatus(result.updated > 0 ? `${result.updated}件整理` : "整理済み");
    } catch {
      setAreaCleanupStatus("整理失敗");
    } finally {
      setAreaCleanupRunning(false);
    }
  }


  return (
    <div className="min-h-screen bg-background">
      <div className="relative overflow-hidden border-b">
        <div className="absolute inset-0">
          <img src={heroImage} alt="レストランの写真" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-background/80" />
        </div>
        <div className="container relative mx-auto max-w-6xl px-3 py-5 sm:px-4 sm:py-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">お店リスト</h1>
              <p className="text-sm text-muted-foreground">行ったお店と行きたいお店を、すぐ探してすぐ追加。</p>
            </div>
            <div className="grid grid-cols-3 gap-2 rounded-md border bg-card/90 p-2 text-center shadow-card">
              <div>
                <div className="text-lg font-semibold">{visitedRestaurants.length}</div>
                <div className="text-[11px] text-muted-foreground">訪問済み</div>
              </div>
              <div>
                <div className="text-lg font-semibold">{wishlistRestaurants.length}</div>
                <div className="text-[11px] text-muted-foreground">行きたい</div>
              </div>
              <div>
                <div className={cn("text-lg font-semibold", scoreToneClass(averageRating as number))}>{averageRating}</div>
                <div className="text-[11px] text-muted-foreground">平均点</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-3 sm:px-4 py-5">
        {/* Stats Cards */}
        <div className="hidden">
          <Card className="bg-card/95 backdrop-blur-sm border-0 shadow-warm p-4 sm:p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MapPin className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-card-foreground">訪問済み</h3>
            </div>
            <p className="text-2xl font-bold text-primary">{visitedRestaurants.length}</p>
            <p className="text-sm text-muted-foreground">訪れたお店</p>
          </Card>

          <Card className="bg-card/95 backdrop-blur-sm border-0 shadow-warm p-4 sm:p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Heart className="h-5 w-5 text-accent" />
              <h3 className="font-semibold text-card-foreground">行きたい</h3>
            </div>
            <p className="text-2xl font-bold text-accent">{wishlistRestaurants.length}</p>
            <p className="text-sm text-muted-foreground">行ってみたいお店</p>
          </Card>

          <Card className="bg-card/95 backdrop-blur-sm border-0 shadow-warm p-4 sm:p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-card-foreground">平均評価</h3>
            </div>
            <p className={"text-2xl font-bold " + scoreToneClass(averageRating as number)}>{averageRating}</p>
            <p className="text-sm text-muted-foreground">平均総合点（0–20）</p>
          </Card>
        </div>

        {/* Rankings Hero (top spotlight) */}
        <div className="hidden">
          {(() => {
            const defs = buildAllRankingDefs(visitedRestaurants).slice();
            for (let i = defs.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [defs[i], defs[j]] = [defs[j], defs[i]];
            }
            const icons = [Crown, Trophy, Medal];
            return defs.slice(0, 3).map((d, i) => {
              const Icon = icons[i] || Trophy;
              const items = d.compute(visitedRestaurants).slice(0, 3);
              return (
                <Link key={d.key} to={`/rankings/${d.key}`}>
                  <Card className="bg-card/95 backdrop-blur-sm border-0 shadow-warm p-4 sm:p-6 text-center hover:shadow-xl transition">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Icon className={cn("h-5 w-5", i===0?"text-yellow-500": i===1?"text-amber-600":"text-orange-500")} />
                      <h3 className="font-semibold text-card-foreground">{d.title}</h3>
                    </div>
                    <ol className="text-sm list-decimal list-inside space-y-1 text-card-foreground/90">
                      {items.length === 0 ? (
                        <li className="text-muted-foreground">データがありません</li>
                      ) : items.map((it) => (
                        <li key={it.restaurant.id} className="truncate">{it.restaurant.name}</li>
                      ))}
                    </ol>
                  </Card>
                </Link>
              );
            });
          })()}
        </div>

        {/* Action Bar */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="grid w-full grid-cols-2 gap-2 sm:w-auto">
            <Button
              variant={activeTab === "visited" ? "default" : "secondary"}
              onClick={() => setActiveTab("visited")}
              className={activeTab === "visited" ? "bg-gradient-primary hover:shadow-soft transition-all duration-300" : ""}
            >
              <MapPin className="h-4 w-4 mr-2" />
              訪問済み {visitedRestaurants.length}
            </Button>
            <Button
              variant={activeTab === "wishlist" ? "default" : "secondary"}
              onClick={() => setActiveTab("wishlist")}
              className={activeTab === "wishlist" ? "bg-gradient-accent hover:shadow-soft transition-all duration-300" : ""}
            >
              <Heart className="h-4 w-4 mr-2" />
              行きたい {wishlistRestaurants.length}
            </Button>
          </div>

          <div className="grid w-full grid-cols-[1fr_auto] items-center gap-2 sm:flex sm:w-auto">
            <NearbySuggest />
            <Button className="inline-flex bg-gradient-primary hover:shadow-soft transition-all duration-300" onClick={() => navigate("/add")}>
              <Plus className="h-4 w-4 mr-2" />
              追加
            </Button>
          </div>
        </div>

        {/* Rankings PickUp */}
        <div className="hidden">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-card-foreground">ランキング PickUp</h2>
            <Link className="text-sm text-primary underline" to="/rankings">すべて見る</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(() => {
              const defs = buildAllRankingDefs(visitedRestaurants).slice();
              // ランダムに3つ
              for (let i = defs.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [defs[i], defs[j]] = [defs[j], defs[i]];
              }
              const picks = defs.slice(0, Math.min(3, defs.length));
              return picks.map((d) => {
                const items = visitedRestaurants.length ? d.compute(visitedRestaurants).slice(0,3) : [];
                return (
                  <Link key={d.key} to={`/rankings/${d.key}`} className="block">
                    <Card className="p-3 h-full hover:shadow-warm transition border-0 shadow-card bg-gradient-warm">
                      <div className="font-semibold mb-1">{d.title}</div>
                      <div className="text-xs text-muted-foreground mb-2 line-clamp-1">{d.description}</div>
                      <ol className="text-sm space-y-1 list-decimal list-inside">
                        {items.length === 0 ? (
                          <li className="text-muted-foreground">データがありません</li>
                        ) : items.map((it, idx) => (
                          <li key={it.restaurant.id} className="truncate">{it.restaurant.name}</li>
                        ))}
                      </ol>
                    </Card>
                  </Link>
                );
              });
            })()}
          </div>
        </div>

        {/* Filters */}
        <Card className="p-3 sm:p-4 mb-4 border-0 shadow-warm">
          <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-primary" />
                <span className="text-sm text-card-foreground font-medium">検索・絞り込み・並び替え</span>
              </div>
              <CollapsibleTrigger asChild>
                <Button size="sm" variant="outline" className="sm:hidden">
                  {filtersOpen ? '閉じる' : '開く'}
                </Button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent className="data-[state=closed]:hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
              <Input
                placeholder="店名・メモで検索"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border-input h-9"
              />
              <Select value={filterCuisine} onValueChange={setFilterCuisine}>
                <SelectTrigger className="border-input h-9"><SelectValue placeholder="料理" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべての料理</SelectItem>
                  {cuisines.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterPref} onValueChange={(value) => { setFilterPref(value); setFilterMuni('all'); }}>
                <SelectTrigger className="border-input h-9"><SelectValue placeholder="都道府県" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべての都道府県</SelectItem>
                  {prefectures.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterMuni} onValueChange={setFilterMuni}>
                <SelectTrigger className="border-input h-9"><SelectValue placeholder="市区町村" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべての市区町村</SelectItem>
                  {municipalities.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortKey} onValueChange={(v) => setSortKey(v as any)}>
                <SelectTrigger className="border-input h-9"><SelectValue placeholder="並び替え" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">更新（新しい順）</SelectItem>
                  <SelectItem value="rating_desc">評価（高い順）</SelectItem>
                  <SelectItem value="rating_asc">評価（低い順）</SelectItem>
                  <SelectItem value="price_asc">価格（安い順）</SelectItem>
                  <SelectItem value="price_desc">価格（高い順）</SelectItem>
                  <SelectItem value="name_asc">名前（昇順）</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mt-3 items-center">
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground min-w-[5rem] sm:min-w-[6rem]">最低総合点</span>
                <Select value={String(minRating)} onValueChange={(v) => setMinRating(Number(v))}>
                  <SelectTrigger className="h-9 w-[90px] border-input"><SelectValue placeholder="0" /></SelectTrigger>
                  <SelectContent>
                    {[0,5,10,15,20].map(n => (<SelectItem key={n} value={String(n)}>{n}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:justify-self-end lg:col-start-4">
                <div className="flex flex-wrap gap-2 sm:justify-end">
                  <Button variant="outline" onClick={() => { setSearch(""); setFilterCuisine('all'); setFilterPref('all'); setFilterMuni('all'); setMinRating(0); setSortKey('newest'); }}>
                    クリア
                  </Button>
                  <Button variant="secondary" disabled={areaCleanupRunning} onClick={runAreaCleanup}>
                    {areaCleanupRunning ? '整理中' : (areaCleanupStatus || '地域表記を整理')}
                  </Button>
                </div>
              </div>
            </div>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {activeFilterChips.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.clear}
                className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground shadow-sm"
              >
                {chip.label}
                <X className="h-3 w-3" />
              </button>
            ))}
            <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setFilterCuisine('all'); setFilterPref('all'); setFilterMuni('all'); setMinRating(0); setSortKey('newest'); }}>
              すべて解除
            </Button>
          </div>
        )}

        {/* Pager (top) */}
        <div className="flex flex-col gap-2 mb-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-muted-foreground hidden sm:block whitespace-nowrap">{total}件中 {start + 1}–{Math.min(end, total)} を表示</div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="h-8 w-[110px] border-input text-xs">
                <SelectValue placeholder="件数" />
              </SelectTrigger>
              <SelectContent>
                {[6,12,18,24,36,48].map(n => (
                  <SelectItem key={n} value={String(n)}>{n}/ページ</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex flex-wrap items-center justify-center gap-1 text-sm sm:justify-end">
              <Button size="sm" variant="outline" onClick={() => setPage(1)} disabled={safePage<=1} aria-label="最初へ">
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPage(safePage-1)} disabled={safePage<=1} aria-label="前へ">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-1 min-w-[3.5rem] text-center">{safePage} / {totalPages}</span>
              <Button size="sm" variant="outline" onClick={() => setPage(safePage+1)} disabled={safePage>=totalPages} aria-label="次へ">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPage(totalPages)} disabled={safePage>=totalPages} aria-label="最後へ">
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="text-xs text-muted-foreground w-full whitespace-nowrap text-right sm:hidden">{start + 1}–{Math.min(end, total)}/{total}</div>
          </div>
        </div>

        {/* Restaurant Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          {paged.map((restaurant) => (
            <RestaurantCard
              key={restaurant.id}
              restaurant={restaurant}
              onClick={() => {
                const qs = searchParams.toString();
                navigate(qs ? `/view/${restaurant.id}?${qs}` : `/view/${restaurant.id}`);
              }}
            />
          ))}
        </div>

        {/* Pager (bottom) */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-2 mt-3 sm:justify-end">
            <div className="flex flex-wrap items-center justify-center gap-1 text-sm sm:justify-end">
              <Button size="sm" variant="outline" onClick={() => setPage(1)} disabled={safePage<=1} aria-label="最初へ">
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPage(safePage-1)} disabled={safePage<=1} aria-label="前へ">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-1 min-w-[3.5rem] text-center">{safePage} / {totalPages}</span>
              <Button size="sm" variant="outline" onClick={() => setPage(safePage+1)} disabled={safePage>=totalPages} aria-label="次へ">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPage(totalPages)} disabled={safePage>=totalPages} aria-label="最後へ">
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredSorted.length === 0 && (
          <div className="text-center py-12">
            <div className="text-muted-foreground mb-4">
              {activeTab === "visited" ? (
                <>
                  <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">条件に合う訪問済みのお店がありません</p>
                  <p className="text-sm">検索条件を緩めるか、最初のお店を追加しましょう</p>
                </>
              ) : (
                <>
                  <Heart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">条件に合う行きたいお店がありません</p>
                  <p className="text-sm">検索条件を緩めるか、行きたいお店を追加してみましょう</p>
                </>
              )}
            </div>
            <Button className="bg-gradient-primary hover:shadow-soft transition-all duration-300" onClick={() => navigate("/add") }>
              <Plus className="h-4 w-4 mr-2" />
              最初のお店を追加
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
